import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import { X } from "lucide-react";
import { useTheme } from "../../../../core/providers/ThemeProvider";
import { DialogWrapper } from "./DialogWrapper";
import acceptIcon from "../../../../assets/accept.svg";
import rejectIcon from "../../../../assets/reject.svg";
import { useTranslation } from "../../../hooks/useTranslation";

interface OfferResultDialogProps {
  open: boolean;
  onClose: () => void;
  variant: "accepted" | "rejected";
  role: "sender" | "traveler";
  onAction?: () => void;
  orderDetails?: {
    orderCode?: string;
    name?: string;
    itineraryName?: string;
    itemName?: string;
    shipmentName?: string;
    categoryName?: string;
    reason?: string;
  };
}

const OfferResultDialog: React.FC<OfferResultDialogProps> = ({
  open,
  onClose,
  variant,
  role,
  onAction,
  orderDetails,
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const primaryColor = theme?.colors?.primary || "#2B7A4B";
  const isAccepted = variant === "accepted";
  const isSender = role === "sender";

  const icon = isAccepted ? acceptIcon : rejectIcon;

  const getTitleKey = () => {
    if (isSender) {
      return isAccepted
        ? "offerNotifications.sender.senderConfirmed.title"
        : "offerNotifications.sender.offerRejectedDialog.title";
    }
    return isAccepted
      ? "offerNotifications.traveler.offerAcceptedDialog.title"
      : "offerNotifications.traveler.offerRejectedDialog.title";
  };

  const getMessage = () => {
    if (isSender && isAccepted) {
      return t("offerNotifications.sender.senderConfirmed.message", {
        itemName: orderDetails?.itemName || "your shipment",
        travelerName: orderDetails?.name || "The traveler",
        categoryName: orderDetails?.categoryName || "your item",
      });
    }
    if (isSender && !isAccepted) {
      return orderDetails?.reason
        ? t("offerNotifications.sender.offerRejectedDialog.message", {
          name: orderDetails?.name || "The traveler",
            itemName: orderDetails?.itemName || orderDetails?.shipmentName || "your item",
            reason: orderDetails.reason,
          })
        : t("offerNotifications.sender.offerRejectedDialog.messageNoReason", {
          name: orderDetails?.name || "The traveler",
            itemName: orderDetails?.itemName || orderDetails?.shipmentName || "your item",
            reason: orderDetails.reason,
          });
    }
    if (!isSender && isAccepted) {
      return t("offerNotifications.traveler.offerAcceptedDialog.message", {
        name: orderDetails?.name || "The sender",
        itineraryName: orderDetails?.itineraryName || "this itinerary",
        categoryName: orderDetails?.categoryName || "your item",
      });
    }
    // traveler rejected
    return orderDetails?.reason
      ? t("offerNotifications.traveler.offerRejectedDialog.message", {
           name: orderDetails?.name || "The sender",
          itineraryName: orderDetails?.itineraryName || "your itinerary",
          reason: orderDetails.reason,
        })
      : t("offerNotifications.traveler.offerRejectedDialog.messageNoReason", {
            name: orderDetails?.name || "The sender",
          itineraryName: orderDetails?.itineraryName || "your itinerary",
          reason: orderDetails.reason,
        });
  };

  const getCloseButtonText = () => {
    return isSender
      ? t("offerNotifications.common.okay")
      : t("offerNotifications.common.close");
  };

  const getActionButtonText = () => {
    if (isSender) {
      return isAccepted
        ? t("offerNotifications.sender.senderConfirmed.viewDetails")
        : t("offerNotifications.common.viewTravelers");
    }
    return isAccepted
      ? t("offerNotifications.common.viewDetails")
      : t("offerNotifications.common.viewSenders");
  };

  // Accepted dialogs use a different icon placement style than rejected
  const isRejectedStyle = !isAccepted;

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby="offer-result-title"
      aria-describedby="offer-result-description"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow:
            "0px 20px 24px -4px rgba(10, 13, 18, 0.08), 0px 8px 8px -4px rgba(10, 13, 18, 0.03)",
          width: { xs: "90%", sm: "auto" },
          minWidth: { sm: "340px" },
          maxWidth: { xs: "100%", sm: "480px" },
        },
      }}
    >

      {/* Main Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: isAccepted ? "flex-start" : "center",
          padding: "32px 24px 24px",
          position: "relative",
          zIndex: isAccepted ? 2 : 1,
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={onClose}
          aria-label="Close dialog"
          sx={{
            position: "absolute",
            right: "16px",
            top: "16px",
            width: "44px",
            height: "44px",
            borderRadius: "8px",
            zIndex: isAccepted && isSender ? 3 : 2,
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <X size={24} color="#A4A7AE" />
        </IconButton>

        {/* Icon */}
        {isAccepted ? (
          isSender ? (
            // SenderOfferAccepted style: absolute positioned in corner
            <Box
              component="img"
              src={icon}
              alt="Accept"
              sx={{
                width: "48px",
                height: "48px",
                position: "absolute",
                top: "24px",
                left: "24px",
                zIndex: 2,
              }}
            />
          ) : (
            // TravelerOfferApproved style: absolute positioned in top-left corner
            <Box
              component="img"
              src={icon}
              alt="Accept"
              sx={{
                width: "48px",
                height: "48px",
                position: "absolute",
                top: "24px",
                left: "24px",
                zIndex: 2,
              }}
            />
          )
        ) : (
          // Rejected style: absolute positioned in top-left
          <Box
            component="img"
            src={icon}
            alt=""
            sx={{
              position: "absolute",
              top: "24px",
              left: "24px",
              width: "48px",
              height: "48px",
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
        )}

        {/* Title and Message */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: isAccepted ? "flex-start" : "center",
            gap: "12px",
            width: "100%",
            textAlign: isAccepted ? "left" : "center",
            mb: isRejectedStyle ? 4 : 3,
            mt: "52px",
          }}
        >
          <Typography
            id="offer-result-title"
            sx={{
              fontFamily: "Poppins",
              fontWeight: isAccepted ? 500 : 600,
              fontSize: "22px",
              lineHeight: "32px",
              color: "#181D27",
            }}
          >
            {t(getTitleKey())}
          </Typography>
          <Typography
            id="offer-result-description"
            component="div"
            sx={{
              fontFamily: "Poppins",
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "21px",
              color: "#667071",
              px: isAccepted ? 0 : 2,
              ...(isRejectedStyle && { whiteSpace: "pre-wrap" }),
              "& strong": { fontWeight: 600, color: "#181D27" },
            }}
            dangerouslySetInnerHTML={{
              __html: getMessage()
                .replace(/\*\*("?[^*]+"?)\*\*/g, "<strong>$1</strong>")
                .replace(/\n/g, "<br/>"),
            }}
          />
        </Box>

        {/* Actions */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "12px",
            width: "100%",
            ...(isAccepted && { mt: 1 }),
          }}
        >
          <Button
            onClick={onClose}
            sx={{
              flex: 1,
              padding: "12px 20px",
              height: "44px",
              background: "#FFFFFF",
              border: "1px solid #D9DADF",
              borderRadius: "8px",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "14px",
              color: "#484A54",
              textTransform: "none",
              "&:hover": {
                background: "#F5F5F5",
              },
            }}
          >
            {getCloseButtonText()}
          </Button>
          {onAction && (
            <Button
              onClick={onAction}
              sx={{
                flex: 1,
                padding: { xs: "8px 12px", sm: "12px 20px" },
                minHeight: "44px",
                background: primaryColor,
                borderRadius: "8px",
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: { xs: "12px", sm: "14px" },
                color: "#FFFFFF",
                textTransform: "none",
                "&:hover": {
                  background: `${primaryColor}DD`,
                },
              }}
            >
              {getActionButtonText()}
            </Button>
          )}
        </Box>
      </Box>
    </DialogWrapper>
  );
};

export default OfferResultDialog;
